import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import { Button, DropDown } from 'Common/Supplyvue-ui'
import { useEffect, useState } from 'react'

export type FreezeOption = 'all-min' | 'edited-only' | 'all-mixed'

export type FreezeOptionsResult = {
  option: FreezeOption
  weeks?: number
}

type WeekMenuItem = { key: string; value: number }

type FreezeOptionsDialogProps = {
  open: boolean
  weekOptions: WeekMenuItem[]
  initialWeeks: number
  isSaving: boolean
  onCancel: () => void
  onConfirm: (result: FreezeOptionsResult) => void
}

const OPTION_LABELS: Record<
  FreezeOption,
  { title: string; description: string; requiresWeeks: boolean }
> = {
  'all-min': {
    title: 'Freeze all SKUs for a minimum of X weeks',
    description:
      'All SKUs are frozen for the greater of their current PLT or X weeks.',
    requiresWeeks: true,
  },
  'edited-only': {
    title: 'Freeze edited SKUs to max edited week or current PLT',
    description:
      'Only edited SKUs are frozen — each up to the greater of its current PLT or the last week you edited for that SKU.',
    requiresWeeks: false,
  },
  'all-mixed': {
    title: 'Freeze all SKUs to max of edited week, current PLT or X weeks',
    description:
      'Every SKU is frozen — for edited SKUs, up to the greatest of its edited week, current PLT or X; for unedited SKUs, the greater of current PLT or X.',
    requiresWeeks: true,
  },
}

export const FreezeOptionsDialog = ({
  open,
  weekOptions,
  initialWeeks,
  isSaving,
  onCancel,
  onConfirm,
}: FreezeOptionsDialogProps) => {
  const [option, setOption] = useState<FreezeOption>('all-min')
  const [weeks, setWeeks] = useState<number>(initialWeeks)

  useEffect(() => {
    if (open) {
      setOption('all-min')
      setWeeks(initialWeeks)
    }
  }, [open, initialWeeks])

  const requiresWeeks = OPTION_LABELS[option].requiresWeeks

  const handleConfirm = () => {
    if (isSaving) return
    onConfirm({
      option,
      weeks: requiresWeeks ? weeks : undefined,
    })
  }

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Save planned production</DialogTitle>
      <DialogContent dividers>
        <Box mb={2}>
          <Typography variant="body2" color="text.secondary">
            Choose how SKUs should be frozen before re-running the plan.
          </Typography>
        </Box>

        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={option}
            onChange={(e) => setOption(e.target.value as FreezeOption)}
          >
            {(Object.keys(OPTION_LABELS) as FreezeOption[]).map((value) => (
              <Box key={value} mb={1.5}>
                <FormControlLabel
                  value={value}
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {OPTION_LABELS[value].title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {OPTION_LABELS[value].description}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            ))}
          </RadioGroup>
        </FormControl>

        {requiresWeeks && (
          <Box sx={{ mt: 2, maxWidth: 240 }}>
            <DropDown
              value={weeks}
              menuItems={weekOptions}
              label="Minimum weeks (X)"
              onChange={(e) => setWeeks(e.target.value as number)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isSaving} variant="text">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isSaving}
          variant="contained"
          color="primary"
        >
          {isSaving ? 'Saving...' : 'OK'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
